<template>
  <div class="coherence-evaluation-page">
    <!-- ──────────────────────────────────────────────
         HEADER · title (+ scope selector with analysisGroups on)
         ────────────────────────────────────────────── -->
    <div class="ve-header">
      <div class="ve-header__title">
        <v-icon left color="primary">mdi-shield-alert</v-icon>
        <span>QA Review · Lie evaluation</span>
      </div>
      <template v-if="groupsEnabled">
        <v-spacer />
        <div class="ve-scope">
          <v-btn-toggle
            :value="scopeKind"
            mandatory
            dense
            class="ve-scope-toggle"
            @change="onScopeKindChange"
          >
            <v-btn value="global" small text>Global</v-btn>
            <v-btn value="group" small text :disabled="!groups.length">Group</v-btn>
          </v-btn-toggle>
          <v-select
            v-if="scopeKind === 'group'"
            :value="scopeGroupId"
            :items="groupItems"
            item-text="text"
            item-value="value"
            dense
            outlined
            hide-details
            class="ve-scope-select"
            :loading="loadingGroups"
            attach
            @change="setScopeGroup"
          />
          <v-btn
            small
            outlined
            color="primary"
            class="ve-scope__compare"
            :disabled="groups.length < 2"
            @click="openComparePicker"
          >
            <v-icon small left>mdi-compare-horizontal</v-icon>
            Compare groups
          </v-btn>
          <v-btn
            small
            color="primary"
            depressed
            class="ve-scope__snapshot"
            :disabled="scopeKind !== 'group' || !scopeGroupId || loading"
            @click="snapshotDialog = true"
          >
            <v-icon small left>mdi-camera-plus-outline</v-icon>
            Save snapshot
          </v-btn>
        </div>
      </template>
    </div>

    <div class="ve-content">
      <!-- Loading -->
      <div v-if="loading" class="ve-loading">
        <v-progress-circular indeterminate color="primary" />
      </div>

      <!-- Error -->
      <v-alert v-else-if="error" type="error" outlined class="ve-error">
        <div class="ve-error__row">
          <span>{{ error }}</span>
          <v-btn small outlined color="error" @click="loadMetrics">
            <v-icon small left>mdi-refresh</v-icon>
            Retry
          </v-btn>
        </div>
      </v-alert>

      <template v-else>
        <GroupMetricsPanel
          :global="global"
          :by-video="byVideo"
          :applied-threshold="appliedThreshold"
          :scope="groupsEnabled ? scope : null"
          :show-buckets="groupsEnabled && scopeKind === 'group'"
          :show-run-column="groupsEnabled && scopeKind === 'group'"
          @apply-threshold="onApplyThreshold"
        />

        <template v-if="groupsEnabled">
          <GroupCompareTable
            v-if="compare.visible"
            :comparison="compare.result"
            :names="compare.names"
            :loading="compare.loading"
            :error="compare.error"
            @close="closeCompare"
          />

          <MetricsSeriesChart
            v-if="scopeKind === 'group' && scopeGroupId"
            :snapshots="snapshots"
            :loading="loadingSnapshots"
            :saving="false"
            @save-snapshot="snapshotDialog = true"
          />

          <GroupsMetricsTable
            :groups="groups"
            :loading="loadingGroups"
            :active-group-id="scopeKind === 'group' ? scopeGroupId : null"
            @select-scope="setScopeGroup"
            @compare="runCompare"
          />
        </template>

        <!-- ──────────────────────────────────────────────
             GESTURE DETECTION · per-gesture precision/recall from analyst feedback
             (the panel hides itself when the backend does not serve it)
             ────────────────────────────────────────────── -->
        <GestureDetectionMetricsPanel :sources="byVideo" />
      </template>
    </div>

    <SaveSnapshotDialog
      v-if="groupsEnabled"
      v-model="snapshotDialog"
      :group-id="scopeGroupId"
      :group-name="scopeGroupName"
      :summary="snapshotSummary"
      :saving="snapshotSaving"
      :error="snapshotError"
      @save="saveSnapshot"
    />

    <v-dialog v-if="groupsEnabled" v-model="comparePicker.open" max-width="520">
      <v-card class="ve-compare-picker">
        <v-card-title class="px-6">
          <h5 class="primary-color mb-0">Compare groups</h5>
          <v-spacer />
          <v-btn icon aria-label="Close" @click="comparePicker.open = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text class="px-6">
          <v-select
            v-model="comparePicker.a"
            :items="groupItems"
            item-text="text"
            item-value="value"
            label="Group A"
            dense
            outlined
            hide-details
            class="mb-3"
            attach
          />
          <v-select
            v-model="comparePicker.b"
            :items="groupItems"
            item-text="text"
            item-value="value"
            label="Group B"
            dense
            outlined
            hide-details
            attach
          />
          <p class="caption grey--text mt-3 mb-0">
            Metrics are compared over the labelled videos both groups share; deltas read B minus A.
          </p>
        </v-card-text>
        <v-divider />
        <v-card-actions class="px-6 py-3">
          <v-spacer />
          <v-btn text class="cancel-modal" @click="comparePicker.open = false">Cancel</v-btn>
          <v-btn
            class="ok-modal ve-compare-picker__run"
            :disabled="!comparePicker.a || !comparePicker.b || comparePicker.a === comparePicker.b"
            @click="runCompareFromPicker"
          >
            Compare
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="snackbar.visible" :timeout="5000" :color="snackbar.color" bottom>
      {{ snackbar.text }}
      <template #action="{ attrs }">
        <v-btn text v-bind="attrs" @click="snackbar.visible = false">Close</v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
import GestureDetectionMetricsPanel from '@/components/analysis/GestureDetectionMetricsPanel.vue';
import GroupCompareTable from '@/components/evaluation/GroupCompareTable.vue';
import GroupMetricsPanel, {
  EMPTY_GLOBAL,
  normalizeMetricsPayload,
} from '@/components/evaluation/GroupMetricsPanel.vue';
import GroupsMetricsTable from '@/components/evaluation/GroupsMetricsTable.vue';
import MetricsSeriesChart from '@/components/evaluation/MetricsSeriesChart.vue';
import SaveSnapshotDialog from '@/components/evaluation/SaveSnapshotDialog.vue';
import featureFlags from '@/config/featureFlags';
import IncoherenceService from '@/services/IncoherenceService';

/**
 * The groups client is only needed with `analysisGroups` on; loading it lazily keeps the
 * flag-off page (and its existing tests) free of the groups module.
 */
async function groupsService() {
  const module = await import('@/services/AnalysisGroupsService');
  return module.default;
}

/**
 * QA Review · Lie evaluation. With `analysisGroups` off this is the global evaluation exactly as
 * before (GroupMetricsPanel renders the same texts and numbers). With the flag on it adds the
 * Global / Group scope (`?group=<id>`), the groups table with Compare, Save snapshot and the
 * snapshot series of the selected group.
 */
export default {
  name: 'CoherenceEvaluation',

  components: {
    GestureDetectionMetricsPanel,
    GroupCompareTable,
    GroupMetricsPanel,
    GroupsMetricsTable,
    MetricsSeriesChart,
    SaveSnapshotDialog,
  },

  data() {
    return {
      loading: true,
      error: null,
      global: JSON.parse(JSON.stringify(EMPTY_GLOBAL)),
      byVideo: [],
      scope: null,
      // null = use the configured default threshold; a number = experiment override.
      appliedThreshold: null,
      // Groups (flag on)
      groups: [],
      loadingGroups: false,
      scopeKind: 'global',
      scopeGroupId: null,
      snapshots: [],
      loadingSnapshots: false,
      snapshotDialog: false,
      snapshotSaving: false,
      snapshotError: '',
      compare: { visible: false, loading: false, error: '', result: null, names: {} },
      comparePicker: { open: false, a: null, b: null },
      snackbar: { visible: false, text: '', color: 'success' },
    };
  },

  computed: {
    groupsEnabled() {
      return Boolean(featureFlags.analysisGroups);
    },

    groupItems() {
      return this.groups.map((group) => ({
        value: Number(group.id),
        text: `${group.name} (${group.members ?? 0} videos)`,
      }));
    },

    scopeGroup() {
      return this.groups.find((group) => Number(group.id) === Number(this.scopeGroupId)) || null;
    },

    scopeGroupName() {
      return this.scopeGroup ? this.scopeGroup.name : 'this group';
    },

    snapshotSummary() {
      if (!this.scopeGroup) return '';
      const method = this.global.methods?.computed_subtraction || {};
      const fmt = (value) => (value === null || value === undefined ? '—' : `${value}%`);
      return `Group ${this.scopeGroup.name} · ${this.global.videos_labeled} labelled videos · ${fmt(
        method.correctness_pct
      )} correct / ${fmt(method.truthful_flagged_pct)} false alarms`;
    },
  },

  watch: {
    '$route.query.group'(value) {
      if (!this.groupsEnabled) return;
      const id = value ? Number(value) : null;
      if (id !== this.scopeGroupId) this.applyScope(id, { updateRoute: false });
    },
  },

  async created() {
    if (this.groupsEnabled) {
      const fromRoute = this.$route && this.$route.query ? this.$route.query.group : null;
      if (fromRoute) {
        this.scopeKind = 'group';
        this.scopeGroupId = Number(fromRoute);
      }
      this.loadGroups();
    }
    await this.loadMetrics();
    if (this.groupsEnabled && this.scopeKind === 'group' && this.scopeGroupId) {
      this.loadSnapshots();
    }
  },

  methods: {
    async loadMetrics() {
      this.loading = true;
      this.error = null;
      try {
        const params = {};
        if (this.appliedThreshold !== null) params.threshold = this.appliedThreshold;
        if (this.groupsEnabled && this.scopeKind === 'group' && this.scopeGroupId) {
          params.groupId = this.scopeGroupId;
        }
        const { data } = await IncoherenceService.getMetrics(params);
        const normalized = normalizeMetricsPayload(data);
        this.global = normalized.global;
        this.byVideo = normalized.byVideo;
        this.scope = normalized.scope;
      } catch (e) {
        this.error = 'Failed to load the lie evaluation metrics.';
      } finally {
        this.loading = false;
      }
    },

    onApplyThreshold(value) {
      this.appliedThreshold = value;
      this.loadMetrics();
    },

    /* Kept for callers/tests that drive the override from the view. */
    applyThreshold(value) {
      this.onApplyThreshold(value);
    },

    resetThreshold() {
      this.onApplyThreshold(null);
    },

    async loadGroups() {
      this.loadingGroups = true;
      try {
        const service = await groupsService();
        const response = await service.list({ limit: 200 });
        const body = response?.data?.data ?? response?.data ?? {};
        const items = Array.isArray(body.items) ? body.items : Array.isArray(body) ? body : [];
        this.groups = items;
        if (this.scopeKind === 'group' && this.scopeGroupId && !this.scopeGroup) {
          // The group in the URL no longer exists: fall back to the global scope.
          this.applyScope(null);
        }
      } catch (error) {
        this.groups = [];
      } finally {
        this.loadingGroups = false;
      }
    },

    async loadSnapshots() {
      if (!this.scopeGroupId) {
        this.snapshots = [];
        return;
      }
      this.loadingSnapshots = true;
      try {
        const service = await groupsService();
        const response = await service.snapshots(this.scopeGroupId);
        const body = response?.data?.data ?? response?.data ?? {};
        this.snapshots = Array.isArray(body.items) ? body.items : Array.isArray(body) ? body : [];
      } catch (error) {
        this.snapshots = [];
      } finally {
        this.loadingSnapshots = false;
      }
    },

    onScopeKindChange(kind) {
      if (kind === 'global') {
        this.applyScope(null);
        return;
      }
      const first = this.scopeGroupId || (this.groups[0] ? Number(this.groups[0].id) : null);
      this.applyScope(first);
    },

    setScopeGroup(groupId) {
      this.applyScope(groupId == null ? null : Number(groupId));
    },

    /** Switches the evaluation scope and reloads metrics (+ snapshots for a group). */
    applyScope(groupId, { updateRoute = true } = {}) {
      this.scopeKind = groupId ? 'group' : 'global';
      this.scopeGroupId = groupId || null;
      if (updateRoute && this.$router && this.$route) {
        const query = { ...this.$route.query };
        if (groupId) query.group = String(groupId);
        else delete query.group;
        this.$router.replace({ path: this.$route.path, query });
      }
      this.loadMetrics();
      if (groupId) this.loadSnapshots();
      else this.snapshots = [];
    },

    openComparePicker() {
      const a = this.scopeKind === 'group' ? this.scopeGroupId : Number(this.groups[0]?.id) || null;
      const b = this.groups.map((group) => Number(group.id)).find((id) => id !== a) || null;
      this.comparePicker = { open: true, a, b };
    },

    runCompareFromPicker() {
      const { a, b } = this.comparePicker;
      this.comparePicker.open = false;
      this.runCompare([a, b]);
    },

    async runCompare(ids) {
      const [a, b] = Array.isArray(ids) ? ids : [];
      if (!a || !b) return;
      const nameOf = (id) => this.groups.find((group) => Number(group.id) === Number(id)) || null;
      this.compare = {
        ...this.compare,
        visible: true,
        loading: true,
        error: '',
        result: null,
        names: { a: nameOf(a), b: nameOf(b) },
      };
      try {
        const params = {};
        if (this.appliedThreshold !== null) params.threshold = this.appliedThreshold;
        const service = await groupsService();
        const response = await service.compare(a, b, params);
        this.compare.result = response?.data?.data ?? response?.data ?? null;
      } catch (error) {
        this.compare.error = 'Could not compare the selected groups.';
      } finally {
        this.compare.loading = false;
      }
    },

    closeCompare() {
      this.compare = { ...this.compare, visible: false, result: null, error: '' };
    },

    async saveSnapshot({ note } = {}) {
      if (!this.scopeGroupId) return;
      this.snapshotSaving = true;
      this.snapshotError = '';
      try {
        const payload = { note };
        if (this.appliedThreshold !== null) payload.threshold = this.appliedThreshold;
        const service = await groupsService();
        await service.createSnapshot(this.scopeGroupId, payload);
        this.snapshotDialog = false;
        this.snackbar = { visible: true, color: 'success', text: 'Snapshot saved' };
        this.loadSnapshots();
        this.loadGroups();
      } catch (error) {
        const data = error?.response?.data;
        this.snapshotError =
          data?.message || data?.error || error?.message || 'Could not save the snapshot.';
      } finally {
        this.snapshotSaving = false;
      }
    },
  },
};
</script>

<style scoped>
.coherence-evaluation-page {
  min-height: 100vh;
}

.ve-header {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 12px 24px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
  background: #fff;
  position: sticky;
  top: 0;
  z-index: 4;
  flex-wrap: wrap;
}

.ve-header__title {
  display: flex;
  align-items: center;
  font-size: 18px;
  font-weight: 700;
  gap: 4px;
}

.ve-scope {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
}

.ve-scope-toggle {
  text-transform: none;
}

.ve-scope-select {
  min-width: 240px;
  max-width: 320px;
}

.ve-content {
  padding: 24px;
  max-width: 1100px;
  margin: 0 auto;
}

.ve-loading {
  display: flex;
  justify-content: center;
  padding: 80px 0;
}

.ve-error__row {
  display: flex;
  align-items: center;
  gap: 16px;
  justify-content: space-between;
  flex-wrap: wrap;
}
</style>
